import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    BriefcaseIcon,
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    ChartBarIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ClockIcon,
    UserIcon
} from '@heroicons/react/24/outline';

interface Task {
    _id: string;
    title: string;
    description: string;
    status: string;
    budgetScenarios: Array<{
        dataCount: number;
        earnings: {
            user: number;
            teamExecutive: number;
            teamLeader: number;
            districtHead: number;
            stateHead: number;
            regionalHead: number;
            admin: number;
        };
    }>;
    assignedUsers: Array<{
        user: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
        };
        dataSubmitted: Array<{
            _id: string;
            data: any;
            submittedAt: string;
            isValidated: boolean;
            validatedBy?: string;
            validatedAt?: string;
        }>;
    }>;
    createdAt: string;
}

interface ValidationStats {
    totalSubmissions: number;
    validated: number;
    pending: number;
    rejected: number;
}

const BDMManagement: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [validationStats, setValidationStats] = useState<ValidationStats>({
        totalSubmissions: 0,
        validated: 0,
        pending: 0,
        rejected: 0
    });

    useEffect(() => {
        if (user?.role === 'bdm') {
            fetchTasks();
        }
    }, [user]);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tasks');
            const tasksData = response.data.data?.tasks || [];
            setTasks(tasksData);

            // Calculate validation stats
            let totalSubmissions = 0;
            let validated = 0;
            let pending = 0;
            let rejected = 0;

            tasksData.forEach((task: Task) => {
                task.assignedUsers.forEach((assignment) => {
                    assignment.dataSubmitted.forEach((submission) => {
                        totalSubmissions++;
                        if (submission.isValidated) {
                            validated++;
                        } else {
                            pending++;
                        }
                    });
                });
            });

            setValidationStats({
                totalSubmissions,
                validated,
                pending,
                rejected
            });
        } catch (error) {
            console.error('Error fetching tasks:', error);
            showToast({ message: 'Error fetching tasks', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDataValidation = async (taskId: string, userId: string, submissionId: string, isValid: boolean) => {
        try {
            await api.put(`/api/tasks/${taskId}/validate-submission`, {
                userId,
                submissionId,
                isValid,
                validatedBy: user?.id
            });

            showToast({ message: `Data ${isValid ? 'validated' : 'rejected'} successfully`, type: 'success' });
            fetchTasks(); // Refresh data
        } catch (error) {
            console.error('Error validating data:', error);
            showToast({ message: 'Error validating data', type: 'error' });
        }
    };

    const filteredTasks = tasks.filter(task => {
        const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            task.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || task.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    if (user?.role !== 'bdm') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">BDM Management</h1>
                    <p className="mt-2 text-gray-600">Manage tasks and validate data submissions</p>
                </div>

                {/* Validation Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Submissions</dt>
                                    <dd className="text-lg font-medium text-gray-900">{validationStats.totalSubmissions}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Validated</dt>
                                    <dd className="text-lg font-medium text-gray-900">{validationStats.validated}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                                    <dd className="text-lg font-medium text-gray-900">{validationStats.pending}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <XCircleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Rejected</dt>
                                    <dd className="text-lg font-medium text-gray-900">{validationStats.rejected}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>

                {/* Tasks List */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Tasks ({filteredTasks.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Users</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredTasks.map((task) => {
                                    const totalSubmissions = task.assignedUsers.reduce((sum, assignment) =>
                                        sum + assignment.dataSubmitted.length, 0
                                    );
                                    const pendingSubmissions = task.assignedUsers.reduce((sum, assignment) =>
                                        sum + assignment.dataSubmitted.filter(sub => !sub.isValidated).length, 0
                                    );

                                    return (
                                        <tr key={task._id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {task.title}
                                                </div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                                    {task.description}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${task.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    task.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {task.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {task.assignedUsers.length}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div>Total: {totalSubmissions}</div>
                                                <div className="text-yellow-600">Pending: {pendingSubmissions}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => setSelectedTask(task)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    View Details
                                                </button>
                                                {pendingSubmissions > 0 && (
                                                    <button
                                                        onClick={() => setSelectedTask(task)}
                                                        className="text-green-600 hover:text-green-900"
                                                    >
                                                        Validate Data
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Task Details Modal */}
                {selectedTask && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {selectedTask.title} - Data Validation
                                    </h3>
                                    <button
                                        onClick={() => setSelectedTask(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <XCircleIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {selectedTask.assignedUsers.map((assignment) => (
                                        <div key={assignment.user._id} className="border rounded-lg p-4">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <UserIcon className="h-6 w-6 text-gray-400" />
                                                <div>
                                                    <h4 className="font-medium text-gray-900">
                                                        {assignment.user.firstName} {assignment.user.lastName}
                                                    </h4>
                                                    <p className="text-sm text-gray-500">{assignment.user.email}</p>
                                                </div>
                                            </div>

                                            {assignment.dataSubmitted.length > 0 ? (
                                                <div className="space-y-3">
                                                    {assignment.dataSubmitted.map((submission, index) => (
                                                        <div key={submission._id} className="bg-gray-50 rounded-lg p-4">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div>
                                                                    <h5 className="font-medium text-gray-900">
                                                                        Submission #{index + 1}
                                                                    </h5>
                                                                    <p className="text-sm text-gray-500">
                                                                        Submitted: {new Date(submission.submittedAt).toLocaleString()}
                                                                    </p>
                                                                    {submission.validatedAt && (
                                                                        <p className="text-sm text-gray-500">
                                                                            Validated: {new Date(submission.validatedAt).toLocaleString()}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${submission.isValidated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                    {submission.isValidated ? 'Validated' : 'Pending'}
                                                                </span>
                                                            </div>

                                                            <div className="mb-4">
                                                                <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
                                                                    {JSON.stringify(submission.data, null, 2)}
                                                                </pre>
                                                            </div>

                                                            {!submission.isValidated && (
                                                                <div className="flex space-x-2">
                                                                    <button
                                                                        onClick={() => handleDataValidation(
                                                                            selectedTask._id,
                                                                            assignment.user._id,
                                                                            submission._id,
                                                                            true
                                                                        )}
                                                                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
                                                                    >
                                                                        <CheckCircleIcon className="h-4 w-4" />
                                                                        <span>Validate</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDataValidation(
                                                                            selectedTask._id,
                                                                            assignment.user._id,
                                                                            submission._id,
                                                                            false
                                                                        )}
                                                                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center space-x-2"
                                                                    >
                                                                        <XCircleIcon className="h-4 w-4" />
                                                                        <span>Reject</span>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 text-center py-4">No data submissions yet</p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={() => setSelectedTask(null)}
                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BDMManagement;
